<template>
  <span>
    <b-button variant="link" class="list-view-icon-btn" v-b-tooltip.hover.bottom title="¿Cómo se usa?" aria-label="¿Cómo se usa?" @click="active = true">
      <AppIcon name="help" />
    </b-button>

    <b-modal v-model="active" size="lg" lazy scrollable>
      <div slot="modal-header" minimize class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="help" /></span>
        <h5 class="app-modal-header__title">Cómo usar Cortes de preparación y reparto</h5>
      </div>

      <h6 class="mb-1">En esta pantalla: {{ current.title }}</h6>
      <p class="text-muted small mb-2">{{ current.purpose }}</p>
      <ol class="pl-3 mb-3">
        <li v-for="(item, index) in current.steps" :key="index" class="mb-1">{{ item }}</li>
      </ol>

      <h6 class="mb-1">El circuito completo</h6>
      <p class="text-muted small mb-2">Un corte es un lote de pedidos de los vendedores que se manda junto al depósito, se controla y se factura de una vez.</p>
      <ol class="cut-help__circuit">
        <li v-for="item in circuit" :key="item.key" :class="{ 'cut-help__circuit--current': item.key === currentStep }">
          <span class="cut-help__circuit-icon"><AppIcon :name="item.icon" /></span>
          <span><strong>{{ item.title }}</strong> {{ item.summary }}</span>
        </li>
      </ol>

      <h6 class="mb-1">Qué significa cada pedido</h6>
      <p class="text-muted small mb-2">El ícono y la franja de color a la izquierda de cada pedido indican en qué está.</p>
      <ul class="cut-help__legend">
        <li v-for="item in legend" :key="item.key">
          <span class="cut-help__legend-icon" :class="'cut-help__legend-icon--' + item.tone"><AppIcon :name="item.icon" :fill="item.fill" /></span>
          <span><strong>{{ item.title }}:</strong> {{ item.text }}</span>
        </li>
      </ul>

      <h6 class="mb-1">Para tener en cuenta</h6>
      <ul class="pl-3 mb-0">
        <li v-for="(item, index) in notes" :key="index" class="mb-1">{{ item }}</li>
      </ul>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" size="sm" @click="active = false"><AppIcon name="close" class="mr-1" />Cerrar</b-button>
      </div>
    </b-modal>
  </span>
</template>

<script>
const HELP = {
  list: {
    title: 'Listado de cortes',
    purpose: 'Muestra los cortes de la última semana con su estado y cuántos pedidos se facturaron.',
    steps: [
      'Tocá un corte para abrirlo y seguir con su preparación o facturación.',
      'Con el botón + armás un corte nuevo.',
      'Desde los filtros podés buscar por número, estado o un rango de fechas más amplio.',
      'Desde ⋮ imprimís la hoja de preparación o el listado de pedidos por vendedor y zona.',
    ],
  },
  create: {
    title: 'Nuevo corte',
    purpose: 'Se eligen los pedidos cerrados que todavía no están en ningún corte.',
    steps: [
      'Indicá hasta qué hora se toman pedidos: es la hora de corte.',
      'Si hace falta, filtrá por vendedor, zona, ciudad o cliente.',
      'Tildá los pedidos, o usá "Seleccionar los N pedidos encontrados" para tomar todos los del filtro.',
      'Tocá "Crear corte". Los pedidos pasan a "en preparación" y ya no se pueden editar desde Pedidos.',
    ],
  },
  preparing: {
    title: 'Preparación y control',
    purpose: 'El depósito revisa cada pedido y carga lo que realmente se entrega.',
    steps: [
      'Imprimí la hoja de preparación (⋮) para armar la mercadería por producto.',
      'Abrí un pedido: cada producto ya trae la cantidad pedida. Cambiá solo lo que falte (nunca más de lo pedido).',
      'En los pesables (fondo amarillo), escribí el peso real de la balanza en kg o g. Debajo de lo pedido está el peso estimado como referencia.',
      'Cuando el pedido está bien, tocá "Controlar". Con "Controlar N pedidos" controlás de una vez todos los que no tienen pesables sin peso.',
      'Con todos los pedidos controlados, tocá "Finalizar preparación".',
    ],
  },
  prepared: {
    title: 'Facturación',
    purpose: 'Se genera una factura por pedido con el punto de venta de cada uno (fiscal o no fiscal).',
    steps: [
      'Tocá "Facturar N pedidos". El proceso corre en segundo plano: podés salir de la pantalla y volver.',
      'Si un pedido da error, el motivo aparece debajo del pedido. Los demás se facturan igual.',
      'Corregí la causa y tocá "Reintentar con error", o quitá ese pedido del corte (⋮) para facturarlo más adelante.',
      'Si hay que corregir cantidades antes de facturar, usá "Reabrir preparación" (⋮).',
    ],
  },
  ready: {
    title: 'Comprobantes',
    purpose: 'Todos los pedidos del corte están facturados.',
    steps: [
      'Tocá "Descargar comprobantes": baja un ZIP con las facturas ordenadas por vendedor, zona, ciudad y pedido.',
      'Si cambió algún comprobante, desde ⋮ podés volver a generar el ZIP.',
    ],
  },
}

export default {
  name: 'OrderCutsHelp',
  props: {
    mode: { type: String, default: 'list' },
    status: { type: String, default: '' },
  },
  data() {
    return {
      active: false,
      circuit: [
        { key: 'create', icon: 'playlist_add', title: 'Armar el corte:', summary: 'elegir los pedidos recibidos hasta una hora.' },
        { key: 'preparing', icon: 'inventory_2', title: 'Preparar y controlar:', summary: 'cargar faltantes y pesos y controlar cada pedido.' },
        { key: 'prepared', icon: 'receipt_long', title: 'Facturar:', summary: 'una factura por pedido, en segundo plano.' },
        { key: 'ready', icon: 'download', title: 'Descargar comprobantes:', summary: 'ZIP con todas las facturas del corte.' },
      ],
      legend: [
        { key: 'pending', icon: 'radio_button_unchecked', tone: 'muted', title: 'Sin controlar', text: 'todavía nadie lo revisó.' },
        { key: 'weigh', icon: 'scale', tone: 'warning', title: 'Falta pesar', text: 'tiene productos pesables sin peso cargado; hasta cargarlos no se puede controlar.' },
        { key: 'ready', icon: 'verified', tone: 'success', fill: true, title: 'Controlado', text: 'revisado y bloqueado. Se reabre desde ⋮ si hay que corregirlo.' },
        { key: 'running', icon: 'sync', tone: 'primary', title: 'Facturando', text: 'la factura se está generando.' },
        { key: 'invoiced', icon: 'receipt_long', tone: 'success', fill: true, title: 'Facturado', text: 'muestra el número de comprobante.' },
        { key: 'error', icon: 'error', tone: 'danger', title: 'Error', text: 'no se pudo facturar; el motivo aparece debajo del pedido.' },
        { key: 'short', icon: 'trending_down', tone: 'danger', title: 'Faltantes', text: 'se va a entregar menos de lo pedido en algún producto.' },
      ],
      notes: [
        'Los productos que se cuentan (U, CAJA) se cargan en números enteros.',
        'Un producto es pesable si así está marcado en Productos o Costos, con su conversión a kilos. Se factura el peso real.',
        'La marca ND indica que el pedido se factura por un punto de venta no fiscal.',
        'Si un pedido cerrado no aparece al armar el corte, puede faltarle el punto de venta, la condición de pago o productos: el aviso amarillo de "Nuevo corte" dice cuáles son.',
        'Un pedido quitado del corte vuelve a quedar cerrado, recupera su stock y aparece para el próximo corte.',
        'Un pedido que ya tiene factura generada no se puede quitar: se reintenta su autorización.',
      ],
    }
  },
  computed: {
    currentStep() {
      if(this.mode === 'create') return 'create'
      if(this.mode === 'cut') return ['ready', 'on_route', 'finished'].includes(this.status) ? 'ready' : (this.status || 'preparing')
      return null
    },
    current() {
      return HELP[this.currentStep] || HELP.list
    },
  },
}
</script>

<style scoped>
.cut-help__circuit, .cut-help__legend { margin: 0 0 1rem; padding: 0; list-style: none; }
.cut-help__circuit li, .cut-help__legend li { display: flex; align-items: flex-start; gap: 0.55rem; margin-bottom: 0.4rem; font-size: 0.85rem; }
.cut-help__circuit li { padding: 0.35rem 0.5rem; border-radius: 0.4rem; color: var(--app-color-text-secondary, #5d6872); }
.cut-help__circuit--current { color: var(--app-color-text, #1c2833) !important; background: var(--app-color-primary-soft, #eef4fa); }
.cut-help__circuit-icon { display: inline-flex; flex-shrink: 0; font-size: 1.1rem; color: var(--app-color-primary, #2b5f8a); }
.cut-help__legend-icon { display: inline-flex; flex-shrink: 0; font-size: 1.1rem; }
.cut-help__legend-icon--muted { color: var(--app-color-text-muted, #9aa5b1); }
.cut-help__legend-icon--warning { color: var(--app-color-warning, #e0a100); }
.cut-help__legend-icon--success { color: var(--app-color-success, #1e7b4a); }
.cut-help__legend-icon--primary { color: var(--app-color-primary, #2b5f8a); }
.cut-help__legend-icon--danger { color: var(--app-color-danger, #c0392b); }
</style>
